(() => {
  const existing = window.DwanCardRowAlign;
  if (existing) {
    existing.refresh();
    return;
  }

  const selector = '[data-dwan-card-row-align]';
  const keys = ['material', 'title', 'price', 'status', 'swatches', 'button', 'colors'];
  const controllers = new Map();
  let discoveryFrame;

  const clearRows = (card) => {
    keys.forEach((key) => card.style.removeProperty(`--dwan-row-${key}`));
  };
  const number = (value) => Number.parseFloat(value) || 0;

  class GridRows {
    constructor(grid) {
      this.grid = grid;
      this.cards = new Set();
      this.items = new Set();
      this.widths = new WeakMap();
      this.frame = undefined;
      this.destroyed = false;
      this.mutations = new MutationObserver(() => this.schedule());
      this.mutations.observe(grid, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        // Never observe our CSS-variable style writes. Color changes replace
        // price children/update label text and are covered above.
        attributeFilter: ['hidden', 'class', 'dir', 'lang'],
      });
      if (typeof ResizeObserver === 'function') {
        this.resize = new ResizeObserver((entries) => {
          let changed = false;
          entries.forEach((entry) => {
            const width = entry.contentRect.width;
            const previous = this.widths.get(entry.target);
            this.widths.set(entry.target, width);
            if (previous === undefined || Math.abs(width - previous) > 0.1) changed = true;
          });
          // Height-only changes from our own min-heights never schedule a loop.
          if (changed) this.schedule();
        });
        this.resize.observe(grid);
      }
      this.schedule();
    }

    schedule() {
      if (this.destroyed || this.frame !== undefined) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = undefined;
        if (!this.grid.isConnected) return;
        this.measure();
      });
    }

    fields(card) {
      const content = card.querySelector(':scope > .card > .card__content');
      const info = content?.querySelector(':scope > .card__information');
      const title = info?.querySelector(':scope > .card__heading > a[data-dwan-grouped-title]');
      if (!title) return null;
      const colors = info.querySelector(':scope > .card-information > .dwan-colors');
      const fields = {
        material: title.querySelector(':scope > .dwan-material-card__material'),
        title: title.querySelector(':scope > .dwan-material-card__device'),
        price: info.querySelector(':scope > .card-information > .price'),
        status: colors?.querySelector(':scope > .dwan-colors__label'),
        swatches: colors?.querySelector(':scope > .dwan-colors__list'),
        button: content.querySelector(':scope > .dwan-material-card__link'),
        colors,
      };
      // Screen-reader-only color captions must not reserve a visible row.
      if (fields.status && window.getComputedStyle(fields.status).position === 'absolute') fields.status = null;
      // Ordinary native cards never receive alignment variables.
      return fields.material && fields.title && fields.price && fields.button ? fields : null;
    }

    measure() {
      const items = Array.from(this.grid.children).filter((item) => item.classList.contains('grid__item'));
      const currentItems = new Set(items);
      this.items.forEach((item) => {
        if (!currentItems.has(item)) this.resize?.unobserve(item);
      });
      items.forEach((item) => {
        if (!this.items.has(item)) this.resize?.observe(item);
      });
      this.items = currentItems;

      // Reset every old card too: a filtered/removed card must not retain the
      // previous row's height, and widths/fonts can make natural heights shrink.
      this.cards.forEach(clearRows);
      const cards = [];
      items.forEach((item) => {
        const card = item.querySelector('.dwan-color-card');
        if (!card) return;
        if (!this.cards.has(card)) clearRows(card);
        const fields = this.fields(card);
        if (fields) cards.push({ item, card, fields });
      });
      this.cards = new Set(cards.map(({ card }) => card));

      const rows = [];
      cards.forEach((entry) => {
        if (!entry.card.getClientRects().length || !entry.item.getClientRects().length) return;
        const visibility = window.getComputedStyle(entry.card).visibility;
        if (visibility === 'hidden' || visibility === 'collapse') return;
        // Siblings share their offset parent. offsetTop deliberately ignores
        // reveal/hover transforms, unlike getBoundingClientRect().top.
        const top = entry.item.offsetTop;
        let row = rows.find((candidate) => Math.abs(candidate.top - top) <= 1);
        if (!row) {
          row = { top, entries: [] };
          rows.push(row);
        }
        row.entries.push(entry);
      });

      // Read natural sizes for every row before writing any final heights.
      const measurements = rows.map((row) => {
        const maximum = Object.fromEntries(keys.map((key) => [key, 0]));
        let colorTopGap = 0;
        let statusBottomGap = 0;
        row.entries.forEach(({ fields }) => {
          keys.forEach((key) => {
            if (key !== 'colors' && fields[key]) {
              maximum[key] = Math.max(maximum[key], Math.ceil(fields[key].getBoundingClientRect().height));
            }
          });
          if (fields.colors) colorTopGap = Math.max(colorTopGap, number(window.getComputedStyle(fields.colors).marginTop));
          if (fields.status) statusBottomGap = Math.max(statusBottomGap, number(window.getComputedStyle(fields.status).marginBottom));
        });
        maximum.colors = maximum.status || maximum.swatches
          ? colorTopGap + maximum.status + statusBottomGap + maximum.swatches
          : 0;
        return { row, maximum };
      });
      measurements.forEach(({ row, maximum }) => {
        row.entries.forEach(({ card }) => {
          keys.forEach((key) => card.style.setProperty(`--dwan-row-${key}`, `${maximum[key]}px`));
        });
      });
    }

    destroy() {
      this.destroyed = true;
      this.mutations.disconnect();
      this.resize?.disconnect();
      if (this.frame !== undefined) window.cancelAnimationFrame(this.frame);
      this.frame = undefined;
      this.cards.forEach(clearRows);
      this.cards.clear();
    }
  }

  const refresh = () => {
    controllers.forEach((controller, grid) => {
      if (!grid.isConnected || !grid.matches(selector)) {
        controller.destroy();
        controllers.delete(grid);
      }
    });
    document.querySelectorAll(selector).forEach((grid) => {
      if (!controllers.has(grid)) controllers.set(grid, new GridRows(grid));
      else controllers.get(grid).schedule();
    });
  };
  const queueDiscovery = () => {
    if (discoveryFrame !== undefined) return;
    discoveryFrame = window.requestAnimationFrame(() => {
      discoveryFrame = undefined;
      refresh();
    });
  };
  const scheduleAll = () => controllers.forEach((controller) => controller.schedule());
  const containsGrid = (node) => node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector));
  const documentChanges = new MutationObserver((records) => {
    const removedGrid = Array.from(controllers.keys()).some((grid) => !grid.isConnected);
    if (removedGrid || records.some((record) => record.type === 'attributes'
      || Array.from(record.addedNodes || []).some(containsGrid))) queueDiscovery();
  });
  documentChanges.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-dwan-card-row-align'],
  });

  window.addEventListener('resize', scheduleAll);
  window.addEventListener('orientationchange', scheduleAll);
  window.addEventListener('load', scheduleAll);
  document.addEventListener('shopify:section:load', queueDiscovery);
  document.addEventListener('shopify:section:reorder', scheduleAll);
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach((controller, grid) => {
      if (event.target?.contains(grid)) {
        controller.destroy();
        controllers.delete(grid);
      }
    });
  });
  if (document.fonts) {
    document.fonts.ready.then(scheduleAll, scheduleAll);
    document.fonts.addEventListener?.('loadingdone', scheduleAll);
    document.fonts.addEventListener?.('loadingerror', scheduleAll);
  }

  window.DwanCardRowAlign = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
